<template>
  <div>
    <transition name="slide-up">
      <div
        v-if="visible"
        ref="popupRef"
        class="notification-popup"
        :style="positionStyle"
      >
        <!-- 标题栏 -->
        <div class="popup-header" @mousedown="startDrag">
          <div class="header-left">
            <i class="el-icon-alarm-clock header-icon"></i>
            <span class="title">{{ task.taskName }}</span>
          </div>
          <div class="header-right">
            <i class="el-icon-s-tools"></i>
            <i class="el-icon-close close-btn" @click="handleClose"></i>
          </div>
        </div>

        <!-- 内容区域 -->
        <div class="popup-content">
          <div class="info-line">
            <i class="el-icon-time icon"></i>
            <span class="label">执行时间:</span>
            <span class="value">{{ formatTime(task.executionTime) }}</span>
          </div>

          <div class="info-line">
            <i class="el-icon-date icon"></i>
            <span class="label">创建时间:</span>
            <span class="value">{{ formatDate(task.createdAt) }}</span>
          </div>

          <!-- 任务内容（固定截断显示） -->
          <div v-if="task.content" class="task-content-area">
            <div class="content-line">
              <i class="el-icon-document icon"></i>
              <div class="content-wrapper">
                <span class="content-label">任务内容:</span>
                <span class="content-text">{{ task.content }}</span>
              </div>
            </div>

            <!-- 固定显示查看详情按钮 -->
            <div class="detail-line">
              <el-button
                type="text"
                size="mini"
                class="detail-btn"
                @click="handleDoneWork"
              >
                完成任务
              </el-button>
              <el-button
                type="text"
                size="mini"
                @click="showFullContent = true"
                class="detail-btn"
              >
                查看详情
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <el-dialog
      :visible.sync="showFullContent"
      :title="task.taskName + ' - 完整内容'"
      width="400px"
      custom-class="content-dialog"
    >
      <div class="full-content">{{ task.content }}</div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showFullContent = false" size="mini">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "TimerDialogComponent",

  props: {
    visible: {
      type: Boolean,
      default: true,
    },

    task: {
      type: Object,
      default: () => ({
        taskId: "",
        taskName: "定时任务",
        status: "SCHEDULED",
        executionTime: "",
        createdAt: "",
        content: "",
      }),
    },

    // 初始位置
    position: {
      type: Object,
      default: () => ({
        right: "20px",
        bottom: "40px",
      }),
    },
  },

  data() {
    return {
      showFullContent: false,
      isDragging: false,
      startX: 0,
      startY: 0,
      startRight: 0,
      startBottom: 0,
      popupWidth: 280,
      popupHeight: 150,
    };
  },

  computed: {
    positionStyle() {
      return {
        right: this.position.right,
        bottom: this.position.bottom,
        position: "fixed",
        zIndex: 9999,
      };
    },
  },

  methods: {
    // 关闭通知
    handleClose() {
      this.$emit("close");
    },

    handleDoneWork() {
      this.$emit("done", this.task);
    },

    // 开始拖拽
    startDrag(event) {
      event.preventDefault();
      this.isDragging = true;

      // 记录起始位置
      this.startX = event.clientX;
      this.startY = event.clientY;

      // 获取当前弹框位置
      const currentRight = parseInt(this.position.right) || 20;
      const currentBottom = parseInt(this.position.bottom) || 80;

      this.startRight = currentRight;
      this.startBottom = currentBottom;

      // 添加事件监听器
      document.addEventListener("mousemove", this.handleDrag);
      document.addEventListener("mouseup", this.stopDrag);

      // 阻止选中文本
      document.body.style.userSelect = "none";
    },

    // 处理拖拽
    handleDrag(event) {
      if (!this.isDragging) return;

      // 计算移动距离
      const deltaX = this.startX - event.clientX;
      const deltaY = this.startY - event.clientY;

      // 计算新位置
      let newRight = this.startRight + deltaX;
      let newBottom = this.startBottom + deltaY;

      // 获取屏幕尺寸
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // 限制边界 - 修复最右边和最底部问题
      // 注意：right和bottom是距离右边界和底边界的距离
      // 所以最小right为0（紧贴右边界），最大right为屏幕宽度-弹框宽度
      const minRight = 0; // 紧贴右边界
      const maxRight = screenWidth - this.popupWidth; // 紧贴左边界

      const minBottom = 0; // 紧贴底边界
      const maxBottom = screenHeight - this.popupHeight; // 紧贴顶边界

      // 限制范围
      newRight = Math.max(minRight, Math.min(newRight, maxRight));
      newBottom = Math.max(minBottom, Math.min(newBottom, maxBottom));

      // 更新位置
      this.position.right = `${newRight}px`;
      this.position.bottom = `${newBottom}px`;
    },

    // 停止拖拽
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.handleDrag);
      document.removeEventListener("mouseup", this.stopDrag);

      // 恢复文本选择
      document.body.style.userSelect = "";
    },

    // 格式化时间
    formatTime(timeStr) {
      if (!timeStr) return "--:--:--";

      if (typeof timeStr === "string" && timeStr.includes(":")) {
        return timeStr;
      }

      const date = new Date(timeStr);
      if (isNaN(date.getTime())) return "时间错误";

      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const seconds = date.getSeconds().toString().padStart(2, "0");

      return `${hours}:${minutes}:${seconds}`;
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return "----/--/--";

      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "日期错误";

      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");

      return `${year}/${month}/${day}`;
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        SCHEDULED: "等待",
        RUNNING: "执行中",
        FINISHED: "完成",
        CANCELLED: "取消",
        FAILED: "失败",
      };
      return statusMap[status] || status;
    },

    // 获取状态类型
    getStatusType(status) {
      const typeMap = {
        SCHEDULED: "warning",
        RUNNING: "primary",
        FINISHED: "success",
        FAILED: "danger",
        CANCELLED: "info",
      };
      return typeMap[status] || "info";
    },
  },

  beforeDestroy() {
    this.stopDrag();
  },
};
</script>

<style lang="scss" scoped>
/* 动画效果 */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
  transform: translateY(0);
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100px);
  opacity: 0;
}

/* 通知弹框样式 */
.notification-popup {
  width: 280px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #ebeef5;
  cursor: move;
  user-select: none;

  &:hover {
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
  }

  .popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: linear-gradient(135deg, #409eff 0%, #337ecc 100%);
    border-radius: 8px 8px 0 0;
    color: white;
    cursor: move;

    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;

      .header-icon {
        font-size: 16px;
      }

      .title {
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 8px;

      .status-tag {
        border: 1px solid rgba(255, 255, 255, 0.3);
        background: rgba(255, 255, 255, 0.2);

        ::v-deep .el-tag__content {
          color: white;
          font-weight: 500;
        }
      }

      .close-btn {
        font-size: 14px;
        cursor: pointer;
        padding: 2px;
        border-radius: 50%;

        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
  }

  .popup-content {
    padding: 12px;
    background: white;
    border-radius: 0 0 8px 8px;

    .info-line {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      font-size: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      .icon {
        width: 16px;
        color: #409eff;
        font-size: 12px;
        margin-right: 6px;
        flex-shrink: 0;
      }

      .label {
        color: #666;
        width: 60px;
        flex-shrink: 0;
      }

      .value {
        color: #333;
        font-weight: 500;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .task-content-area {
      border-top: 1px dashed #eee;

      .content-line {
        display: flex;
        align-items: flex-start;
        font-size: 12px;
        margin-bottom: 8px;

        .icon {
          width: 16px;
          color: #67c23a;
          font-size: 12px;
          margin-right: 6px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .content-wrapper {
          flex: 1;
          display: flex;
          align-items: flex-start;
          min-width: 0;

          .content-label {
            color: #666;
            flex-shrink: 0;
            margin-right: 4px;
            white-space: nowrap;
          }

          .content-text {
            color: #333;
            line-height: 1.4;
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }

      .detail-line {
        text-align: right;
        padding-top: 4px;
        border-top: none;

        .detail-btn {
          padding: 2px 8px;
          font-size: 12px;

          &:hover {
            color: #409eff;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
/* 完整内容弹框样式 */
.content-dialog {
  .el-dialog {
    border-radius: 8px;

    &__header {
      padding: 12px 15px;
      border-bottom: 1px solid #ebeef5;

      .el-dialog__title {
        font-size: 14px;
      }
    }

    &__body {
      padding: 15px;

      .full-content {
        font-size: 13px;
        line-height: 1.5;
        color: #333;
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 400px;
        overflow-y: auto;
      }
    }

    &__footer {
      padding: 10px 15px;
      border-top: 1px solid #ebeef5;
      text-align: center;
    }
  }
}
</style>
